import classNames from 'classnames'
import { usePartnerId } from 'legacy/lut/classes/helpers/partner360/usePartnerId'
import {
  IFormJBPHPContactChangeRequest,
  IFormJBPHPContactRoles,
  JBPHPContactChangeRequestStatus,
  newHpContactRequest,
} from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { HPContactsDuplicateDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsDuplicateDialog/HPContactsDuplicateDialog'
import { HpContactsTicketDeleteDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsTicketDeleteDialog/HpContactsTicketDeleteDialog'
import { HpContactsTicketNotNewDialog } from 'legacy/lut/components/widgets/public/partner360/common/hpContacts/hpContactsTicketNotNewDialog/HpContactsTicketNotNewDialog'
import { P360NotInScopeWidgetPlaceholder } from 'legacy/lut/components/widgets/public/partner360/components/p360NotInScopeWidgetPlaceholder/P360NotInScopeWidgetPlaceholder'
import { saveHPContactChangeRequest } from 'legacy/lut/components/widgets/public/partner360/helpers/hpContactRoles/saveHPContactChangeRequest'
import { useHpContacts } from 'legacy/lut/components/widgets/public/partner360/hooks/hpContacts/useHpContacts'
import { P360Card } from 'legacy/lut/components/widgets/public/partner360/p360Card'
import { P360WidgetPlaceholder } from 'legacy/lut/components/widgets/public/partner360/P360WidgetPlaceholder'
import {
  selectPartnerAccess,
  selectProgramType,
} from 'legacy/lut/features/partner360/configuration'
import { useUserRoles } from 'legacy/lut/hooks/useUserRoles'
import { Table } from 'legacy/lut/library/list'
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { IForm } from 'core-components/classes/models/IForm'
import { Button } from 'core-components/library/button'
import { IColumn } from 'core-components/library/list/types'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { guid } from 'core/classes/helpers/GuidHelper'
import { selectPublishingMode } from 'core/store/selectors/selectPublishingMode'
import { ContactTicketIcon } from 'partner360/common/hpContacts/contactTicketIcon/ContactTicketIcon'
import { HPContactRequestEditor } from 'partner360/features/hpContacts'
import { useLeadPbmId, useSalesTeamRoles } from 'partner360/features/hpContacts/model'
import { getDefaultHpContactRole } from 'partner360/features/hpContacts/model/getDefaultHpContactRole'
import { getHPContactRoleForEditing } from 'partner360/features/hpContacts/model/getHPContactRoleForEditing'
import { useHpContactsRoles } from 'partner360/features/hpContacts/model/useHpContactsRoles'
import { withPartner360Checks } from 'partner360/hoc/withPartner360Checks'
import { removeHpContactRequest } from 'partner360/services/hpContacts/removeHpContactRequest'

import { HelpSection } from '../helpButton'
import { HPContactsRequest } from '../hpContacts/HPContactsRequest'
import { ConfirmationDialog } from '../programsManagementAdmin/ConfirmationDialog'
import { WidgetHeaderWithHelpButton } from '../widgetHeader'

import { PartnerRolesDropdown } from './components/partnerRolesDropdown/PartnerRolesDropdown'
import { IHPContactsTableProps } from './models/hpContactsTableProps'

import styles from './HPContactsTable.module.scss'

const HPContactsTableComponent: FC<IHPContactsTableProps> = ({
  pid,
  widgetParams,
  flexWidth,
  metrics,
  settings,
}) => {
  /** -------------------- State ----------------------- */
  const [currentRole, setCurrentRole] = useState<IFormJBPHPContactRoles>()
  const [currentHPContact, setCurrentHPContact] = useState<IFormJBPHPContactChangeRequest>()
  const [showContactEditorDialog, setShowContactEditorDialog] = useState(false)
  const [showDeleteContactDialog, setShowDeleteContactDialog] = useState(false)
  const [showContactTicketDialog, setShowContactTicketDialog] = useState(false)
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false)
  const [showTicketDeleteDialog, setShowTicketDeleteDialog] = useState(false)
  const [showTicketNotNewDialog, setShowTicketNotNewDialog] = useState(false)
  const [isDuplicationCheckInProgress, setDuplicationCheckInProgress] = useState(false)

  /** -------------------- hooks ----------------------- */
  const partnerId = usePartnerId(widgetParams, pid)
  const programType = useSelector(selectProgramType)
  const { isPartnerUser } = useUserRoles()
  const isEditMode = useSelector(selectPublishingMode)
  const { isDistributor } = useSelector(selectPartnerAccess)
  const user = useSelector(selectUser)
  const [leadPbmId, setLeadPbmId] = useLeadPbmId({
    partnerId,
  })
  const allRoles = useSalesTeamRoles(metrics)
  const { isLoading, partnerRoles, fetchPartnerRoles } = useHpContactsRoles({
    partnerId,
    metrics,
  })
  const {
    jbpHpContactChangeRequestCrud,
    tableFetch,
    getContactsByEmail,
    resetCrudStorage,
    refreshTable,
  } = useHpContacts({
    crudKey: settings?.sourceKey,
    roles: currentRole?.Roles,
    partnerId,
    shouldFetchAllRoles: settings?.fetchAllRoles,
  })

  /** -------------------- Variables ----------------------- */
  const isRetailProgramType = useMemo(() => programType === ProgramTypes.Retail, [programType])
  const isEditAllowed = useMemo(() => isEditMode && !isPartnerUser, [isEditMode, isPartnerUser])

  const currentDefaultHpContactRole = useMemo(() => {
    return getDefaultHpContactRole({ isDistributor, isRetailProgramType })
  }, [isRetailProgramType, isDistributor])

  const columns: IColumn[] = useMemo(() => {
    const base: IColumn[] = [
      {
        field: 'TicketId',
        title: 'partnerContacts-contact',
        priority: 1,
        renderFunction: (item) => {
          return (
            <div className={styles.contact}>
              <ContactTicketIcon item={item} />
              <Tooltip
                inline
                tipContent={() => (
                  <>
                    {item.FirstName ? `${item.FirstName} ` : ''}
                    {item.LastName || ''}
                    {item.Email && (
                      <>
                        <br />
                        {item.Email}
                      </>
                    )}
                  </>
                )}
              >
                <span className={styles.contact__item} tabIndex={0}>
                  {item.FirstName ? `${item.FirstName} ` : ''}
                  {item.LastName || ''}
                </span>
              </Tooltip>
            </div>
          )
        },
      },
      {
        field: 'BusinessUnit',
        title: 'partnerContacts-businessunit',
        priority: 2,
      },
      {
        field: 'LeadPBM',
        title: 'jbp-partner-contacts-leadpbm',
        priority: 3,
        renderFunction: (item, column) => {
          return <div>{item[column.field] ? 'Yes' : 'No'}</div>
        },
      },
      {
        field: 'AssignmentPeriod',
        title: 'jbp-partner-contacts-assignmentperiod',
        priority: 4,
      },
    ]
    if (settings?.showRoleColumn) {
      return [
        ...base,
        {
          field: 'Roles',
          title: 'partnerContacts-roles',
          priority: 2,
        },
      ]
    }
    return base
  }, [settings?.showRoleColumn])

  const rowActions = useMemo(() => {
    return [
      {
        id: 'selectRow',
        action: (item: IFormJBPHPContactChangeRequest) => {
          if (settings?.onSelectRow) {
            settings.onSelectRow(item)
          }
        },
        title: 'Select',
        icon: 'kbase kbase-add kbase-sm',
        isVisible: () => {
          return Boolean(settings?.onSelectRow)
        },
      },
      {
        id: 'updateSalesTeamContact',
        action: (item: IFormJBPHPContactChangeRequest) => {
          setCurrentHPContact({
            ...item,
            RequestAction: 'Update',
          })
          setShowContactTicketDialog(true)
        },
        title: 'Edit',
        icon: 'kbase kbase-edit kbase-sm',
        isVisible: (item: any) => {
          return !item.TicketId
        },
      },
      {
        id: 'deleteSalesTeamContact',
        action: async (item: IFormJBPHPContactChangeRequest) => {
          setCurrentHPContact({
            ...item,
            RequestAction: 'Delete',
          })
          setShowContactTicketDialog(true)
        },
        title: 'Delete',
        icon: 'kbase kbase-delete kbase-sm',
        isVisible: (item: any) => {
          return !item.TicketId
        },
      },
      {
        id: 'deleteTicket',
        action: async (item: IFormJBPHPContactChangeRequest) => {
          if (item.TicketStatus === JBPHPContactChangeRequestStatus.New) {
            setCurrentHPContact({
              ...item,
              RequestAction: 'DeleteTicket',
            })
            setShowTicketDeleteDialog(true)
          } else {
            setShowTicketNotNewDialog(true)
          }
        },
        title: 'Delete Ticket',
        icon: 'kbase kbase-close kbase-sm',
        isVisible: (item: any) => {
          return item.TicketId && item.TicketStatus !== JBPHPContactChangeRequestStatus.WIP
        },
      },
    ]
  }, [settings])

  /** -------------------- Methods ----------------------- */
  const handleDeleteHPContact = useCallback(async () => {
    setShowTicketDeleteDialog(false)
    resetCrudStorage()
    await removeHpContactRequest({
      crud: jbpHpContactChangeRequestCrud,
      entryId: currentHPContact?.TicketId.toString() || '-1',
    })

    refreshTable()
    await fetchPartnerRoles(true)
  }, [
    jbpHpContactChangeRequestCrud,
    resetCrudStorage,
    refreshTable,
    fetchPartnerRoles,
    currentHPContact?.TicketId,
  ])

  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    return () => {
      setCurrentRole(undefined)
    }
  }, [partnerId])

  useEffect(() => {
    if (!partnerRoles.length) {
      return
    }

    if (!currentRole) {
      const defaultRole = partnerRoles.find((it) => it.Roles === currentDefaultHpContactRole)
      setCurrentRole(defaultRole || partnerRoles[0])
      return
    }

    const roleIsNotAvailable = partnerRoles.every((it) => it.Roles !== currentRole.Roles)
    if (roleIsNotAvailable) {
      const defaultRole = partnerRoles.find((it) => it.Roles === currentDefaultHpContactRole)
      setCurrentRole(defaultRole || partnerRoles[0])
      return
    }
  }, [currentRole, partnerRoles, currentDefaultHpContactRole])

  if (isLoading) {
    return <Loader />
  }

  return (
    <P360Card
      header={
        <WidgetHeaderWithHelpButton section={HelpSection.HpContact} align={'left'}>
          jbp-hpcontacttable-title
        </WidgetHeaderWithHelpButton>
      }
      fullHeight={true}
    >
      <div className={styles.root} data-testid={'hpContactsTable-root'}>
        {!isDistributor && !isRetailProgramType && (
          <div className={classNames(styles.infoContainer)}>
            <i className={classNames('kbase kbase-info', styles.infoIcon)} aria-hidden="true" />
            <Translation text={'jbp-hpcontacts-info'} actionsInside />
          </div>
        )}
        <div>
          <RenderWhen condition={!settings?.hideRoleDropdown}>
            <PartnerRolesDropdown
              value={currentRole?.UniqueId}
              roles={partnerRoles}
              onRoleChange={setCurrentRole}
            />
          </RenderWhen>
          <RenderWhen condition={isEditAllowed}>
            <Button
              iconButton="kbase kbase-add"
              onClick={() => {
                setCurrentHPContact({
                  ...newHpContactRequest,

                  FirstName: user.firstName,
                  LastName: user.lastName,
                  Email: user.email,
                  SignId: `new-${guid()}`,

                  Roles: getHPContactRoleForEditing({
                    isDistributor,
                    currentDefaultHpContactRole,
                    currentRole: currentRole?.Roles,
                  }),

                  RequestAction: 'Add',
                })

                setShowContactTicketDialog(true)
              }}
            />
          </RenderWhen>
          <RenderWhen condition={isDuplicationCheckInProgress}>
            <Loader
              className={styles.duplicationCheckLoader}
              dimension={{ width: 34, height: 34 }}
            />
          </RenderWhen>
        </div>
        <div className={styles.container}>
          {currentRole && (
            <Table
              key={tableFetch}
              pagination
              header
              flexWidth={flexWidth}
              columns={columns}
              sourceUpdateCount={tableFetch}
              source={jbpHpContactChangeRequestCrud}
              hasTranslate
              rowActionsVisible={isEditAllowed || settings?.onSelectRow ? 'visible' : 'hidden'}
              rowActions={rowActions}
            />
          )}
          <ModalDialog
            show={showContactEditorDialog}
            size={'sm'}
            content={HPContactsRequest}
            contentProps={{
              partnerId: partnerId,
              item: currentHPContact || {},
              leadId: leadPbmId,
              roles: partnerRoles,
              onApply: async (item: IForm) => {
                const method = !currentHPContact?.EntryId
                  ? jbpHpContactChangeRequestCrud.create
                  : jbpHpContactChangeRequestCrud.update

                const result = await method({ formId: '606', code: 'WW_SalesTeam' }, [item])
                jbpHpContactChangeRequestCrud.removeQueries(true)

                if (result && result.length) {
                  const entryId = result[0]['EntryId'] || 0
                  if (entryId === leadPbmId && !item['LeadPBM']) {
                    setLeadPbmId(-1)
                  }
                  if (leadPbmId === -1 && !!item['LeadPBM']) {
                    setLeadPbmId(entryId)
                  }
                }

                setShowContactEditorDialog(false)
              },
              metrics: metrics,
              flexWidth: flexWidth,
            }}
            onClose={() => setShowContactEditorDialog(false)}
          />
          <ModalDialog
            show={showDeleteContactDialog}
            size={'xs'}
            content={ConfirmationDialog}
            contentProps={{
              text: `Please confirm that you want to delete contact`,
              onClick: async (submit: boolean) => {
                {
                  if (submit) {
                    await jbpHpContactChangeRequestCrud.delete(
                      { formId: '606', code: 'WW_SalesTeam' },
                      currentHPContact || {},
                      'EntryId'
                    )
                  }
                  if (currentHPContact?.LeadPBM) {
                    setLeadPbmId(-1)
                  }
                  setCurrentHPContact(undefined)
                  setShowDeleteContactDialog(false)
                }
              },
            }}
            onClose={() => setShowDeleteContactDialog(false)}
          />
        </div>
        <ModalDialog
          show={showContactTicketDialog}
          size={'sm'}
          content={HPContactRequestEditor}
          contentProps={{
            isVisible: showContactTicketDialog,
            currentHpContactRequest: (currentHPContact || {}) as IFormJBPHPContactChangeRequest,

            partnerId: partnerId as string,
            leadId: leadPbmId,
            currentRole: getHPContactRoleForEditing({
              isDistributor,
              currentDefaultHpContactRole,
              currentRole: currentRole?.Roles,
            }),

            onApply: async (newRequest: IFormJBPHPContactChangeRequest) => {
              setShowContactTicketDialog(false)
              setDuplicationCheckInProgress(true)

              const { hasDuplicate, newRole } = await saveHPContactChangeRequest({
                newRequest,
                resetCrudStorage,
                getContactsByEmail,
                jbpHpContactChangeRequestCrud,
                fetchPartnerRoles,
              })

              setDuplicationCheckInProgress(false)

              if (hasDuplicate) {
                setShowDuplicateDialog(true)
              }

              if (newRole) {
                setCurrentRole(newRole)

                if (newRole.Roles === currentRole?.Roles) {
                  refreshTable()
                }
              }
            },
            onCancel: () => {
              setShowContactTicketDialog(false)
            },
          }}
          onClose={() => setShowContactTicketDialog(false)}
        />
        <HPContactsDuplicateDialog
          isShow={showDuplicateDialog}
          onClose={() => setShowDuplicateDialog(false)}
        />
        <HpContactsTicketNotNewDialog
          isShow={showTicketNotNewDialog}
          onClose={() => setShowTicketNotNewDialog(false)}
        />
        <HpContactsTicketDeleteDialog
          isShow={showTicketDeleteDialog}
          onClose={() => setShowTicketDeleteDialog(false)}
          handleDelete={handleDeleteHPContact}
        />
      </div>
    </P360Card>
  )
}

HPContactsTableComponent.displayName = 'HPContactsTableComponent'

const ProtectedHPContactsTable = withPartner360Checks(HPContactsTableComponent, {
  accessOverlayPlaceholder: <P360WidgetPlaceholder />,
  scopeOverlayPlaceholder: <P360NotInScopeWidgetPlaceholder />,
})

ProtectedHPContactsTable.displayName = `withUserAccessCheck(${HPContactsTableComponent.displayName})`

export const HPContactsTable = memo(
  ProtectedHPContactsTable,
  (previousProps, nextProps) => previousProps.pid === nextProps.pid
)

HPContactsTable.displayName = `memo(${ProtectedHPContactsTable.displayName})`

export default HPContactsTable
